import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { apiClient } from '@services/api'

interface BillRow {
  id: number
  billNo: string
  supplierId: number
  supplierName: string
  billDate: string
  itemCount: number
  taxableAmount: number
  gstRate: number
  gstAmount: number
  amount: number
  amountPaid: number
  balance: number
  paymentStatus: 'Paid' | 'Partial' | 'Unpaid' | 'Draft'
  status: 'Active' | 'Draft' | 'Completed'
}

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

const paymentBadge: Record<BillRow['paymentStatus'], string> = {
  Paid: 'badge-full',
  Partial: 'badge-partial',
  Unpaid: 'badge-unpaid',
  Draft: 'badge-draft',
}

export function PurchaseBillsList() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const params = new URLSearchParams()
  if (q.trim()) params.set('q', q.trim())
  if (status) params.set('status', status)
  if (supplierId) params.set('supplierId', supplierId)
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const { data, isLoading } = useQuery({
    queryKey: ['purchase-bills', q, status, supplierId, from, to],
    queryFn: () => apiClient.get<any>(`/purchase-bills?${params.toString()}`),
  })
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-list'],
    queryFn: () => apiClient.get<any>('/suppliers'),
  })

  const { data: draftsData } = useQuery({
    queryKey: ['purchase-bills-drafts'],
    queryFn: () => apiClient.get<any>('/purchase-bills?status=Draft'),
  })
  const draftCount: number = draftsData?.data?.items?.length || 0
  const bills: BillRow[] = data?.data?.items || []
  const summary = data?.data?.summary || { bills: 0, taxable: 0, gst: 0, amount: 0, paid: 0, balance: 0 }
  const suppliers: Array<{ id: number; name: string }> = suppliersData?.data?.items || []

  return (
    <div>
      <div className="hdr flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1>Purchase Bills</h1>
          <p>Every supplier bill recorded — search by bill or supplier, filter by payment status and date. Click a bill to see the items purchased.</p>
        </div>
        <Link to="/dashboard/procurement/purchase-bills" className="btn-primary">
          + New Purchase Bill
        </Link>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="label">Bills</div>
          <div className="val">{summary.bills}</div>
          <div className="sub">matching filters</div>
        </div>
        <div className="kpi">
          <div className="label">Total Purchases</div>
          <div className="val">{formatINR(summary.amount)}</div>
          <div className="sub">
            {formatINR(summary.taxable || 0)} + GST {formatINR(summary.gst || 0)}
          </div>
        </div>
        <div className="kpi">
          <div className="label">Paid to Suppliers</div>
          <div className="val">{formatINR(summary.paid)}</div>
          <div className="sub up">settled</div>
        </div>
        <div className="kpi">
          <div className="label">Payable Balance</div>
          <div className="val">{formatINR(summary.balance)}</div>
          <div className={`sub ${summary.balance > 0 ? 'down' : 'up'}`}>{summary.balance > 0 ? 'still owed' : 'nothing outstanding'}</div>
        </div>
      </div>

      <div className="toolbar">
        <input type="text" placeholder="Search bill no. or supplier..." value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Payment Status</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Partial">Partially Paid</option>
          <option value="Paid">Paid</option>
          <option value="Draft">Draft</option>
        </select>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
          <option value="">All Suppliers</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name.split(' — ')[0]}
            </option>
          ))}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From date" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To date" />
        {draftCount > 0 && status !== 'Draft' && (
          <button type="button" className="chip" style={{ background: '#FFFBEB', borderColor: '#E9C46A' }} onClick={() => setStatus('Draft')} title="Bills saved as draft — not in stock until submitted" data-testid="drafts-chip">
            Drafts waiting: {draftCount}
          </button>
        )}
        {(q || status || supplierId || from || to) && (
          <button
            type="button"
            className="chip"
            onClick={() => {
              setQ('')
              setStatus('')
              setSupplierId('')
              setFrom('')
              setTo('')
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="card">
        <h2>
          Purchase History <small>— supplier-wise, newest first</small>
        </h2>
        {isLoading ? (
          <div className="text-center py-6 text-ink-soft text-[13px]">Loading bills…</div>
        ) : bills.length === 0 ? (
          <div className="text-center py-6 text-ink-soft text-[13px]">No purchase bills match these filters.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Bill</th>
                <th>Supplier</th>
                <th className="num">Items</th>
                <th className="num">Total Value</th>
                <th className="num">GST</th>
                <th className="num">Grand Total</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr
                  key={b.id}
                  className="cursor-pointer hover:bg-[#FAFAF7]"
                  onClick={() => navigate(`/dashboard/procurement/purchase-bills/${b.id}`)}
                >
                  <td className="whitespace-nowrap">{formatDate(b.billDate)}</td>
                  <td>
                    <Link
                      to={`/dashboard/procurement/purchase-bills/${b.id}`}
                      className="text-primary-500 font-semibold hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {b.billNo}
                    </Link>
                  </td>
                  <td>{b.supplierName}</td>
                  <td className="num">{b.itemCount}</td>
                  <td className="num">{formatINR(b.taxableAmount)}</td>
                  <td className="num">
                    {formatINR(b.gstAmount)}
                    <div className="text-[10.5px] text-ink-soft">{b.gstRate}%</div>
                  </td>
                  <td className="num font-semibold">{formatINR(b.amount)}</td>
                  <td className="num">{formatINR(b.amountPaid)}</td>
                  <td className="num font-semibold">{formatINR(b.balance)}</td>
                  <td>
                    <span className={`badge ${paymentBadge[b.paymentStatus]}`}>{b.paymentStatus === 'Partial' ? 'Partial' : b.paymentStatus}</span>
                  </td>
                  <td>
                    <span className={`badge ${b.status === 'Draft' ? 'badge-draft' : 'badge-ok'}`}>{b.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="hint">
          Paid / Balance come from payments recorded on the Transactions screen. Draft bills haven't touched stock yet — open one to
          review and submit it.
        </div>
      </div>
    </div>
  )
}
